<!-- =========================================================================================
  File Name: WizardStepConfig.vue
  Description: Step 4 - Configure clustering parameters (name, clusters, micro-clusters)
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-12-19
========================================================================================== -->

<template>
	<div class="wizard-step-config">
		<div class="step-header">
			<h2 class="step-title">{{ $t("Wizard.Steps.Config.MainTitle") }}</h2>
			<p class="step-description">{{ $t("Wizard.Steps.Config.Description") }}</p>
		</div>

		<div class="step-content">
			<!-- Summary Panel -->
			<div class="summary-panel">
				<h3 class="panel-title">{{ $t("Wizard.SelectionSummary") }}</h3>
				<div class="summary-grid">
					<div class="summary-item">
						<feather-icon icon="MapPinIcon" svgClasses="w-6 h-6 text-primary" />
						<div class="summary-content">
							<span class="summary-value">{{ wizardData.selectedPoints.length }}</span>
							<span class="summary-label">{{ $t("Wizard.PointsSelected") }}</span>
						</div>
					</div>
					<div class="summary-item">
						<feather-icon icon="PackageIcon" svgClasses="w-6 h-6 text-success" />
						<div class="summary-content">
							<span class="summary-value">{{ wizardData.totalDemand || totalDemand }}</span>
							<span class="summary-label">{{ $t("Clustering.TotalDemand") }}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Configuration Form - Single Page Design -->
			<div class="config-form">
				<vx-card>
					<!-- General Configuration Section -->
					<div class="config-section">
						<div class="section-header">
							<feather-icon icon="SettingsIcon" svgClasses="w-6 h-6 text-primary" />
							<h3 class="section-title">{{ $t("Clustering.GeneralConfig") }}</h3>
						</div>

						<!-- Clustering Name -->
						<div class="form-field">
							<label class="field-label required">{{ $t("Clustering.Name") }}</label>
							<vs-input
								v-model="clusteringName"
								:placeholder="$t('Clustering.NamePlaceholder')"
								icon-pack="feather"
								icon="icon-tag"
								class="w-full"
								@input="updateData"
							/>
							<small class="field-help">{{ $t("Wizard.Steps.Config.NameHelp") }}</small>
						</div>

						<!-- Total Clusters -->
						<div class="form-field">
							<label class="field-label required">{{ $t("Clustering.TotalClusters") }}</label>
							<div class="input-with-info">
								<vs-input
									v-model.number="totalClusters"
									type="number"
									min="1"
									max="10000"
									class="cluster-input"
									@input="validateAndUpdateClusters"
								/>
								<div class="input-info">
									<vs-chip :color="clusterConfig.length === totalClusters ? 'success' : 'primary'">
										{{ clusterConfig.length }} / {{ totalClusters }}
										{{ $t("Clustering.ClustersCreated") || "created" }}
									</vs-chip>
								</div>
							</div>
						</div>

						<!-- Total Micro Clusters -->
						<div class="form-field">
							<label class="field-label">{{
								$t("Clustering.MicroClustersPerCluster") || "Micro-Clusters per Cluster"
							}}</label>
							<div class="input-with-info">
								<vs-input
									v-model.number="totalMicroClusters"
									type="number"
									min="1"
									max="10000"
									class="w-full"
									@input="validateAndUpdateMicroClusters"
								/>
								<div class="input-info">
									<vs-chip color="success"
										>{{ totalMicroClustersAllocated }}
										{{ $t("Clustering.Total") || "total" }}</vs-chip
									>
								</div>
							</div>
							<small class="field-help">{{ $t("Clustering.MicroClustersHelp") }}</small>
						</div>

						<!-- Enable Capacities Toggle (Conditional) -->
						<transition name="slide-down">
							<div v-if="totalMicroClusters > 0" class="form-field">
								<div class="toggle-field">
									<div class="toggle-header">
										<feather-icon icon="BarChart2Icon" svgClasses="w-5 h-5 mr-3 text-primary" />
										<span class="font-semibold">{{
											$t("Clustering.EnableCapacities") || "Enable Capacities Configuration"
										}}</span>
									</div>
									<vs-switch v-model="capacitiesEnabled">
										<span slot="on">{{ $t("Clustering.Enabled") || "ON" }}</span>
										<span slot="off">{{ $t("Clustering.Disabled") || "OFF" }}</span>
									</vs-switch>
								</div>
							</div>
						</transition>
					</div>

					<!-- Divider -->
					<vs-divider v-if="totalMicroClusters > 0 && capacitiesEnabled" />

					<!-- Detailed Configuration Section (Conditional) -->
					<transition name="slide-down">
						<div v-if="totalMicroClusters > 0 && capacitiesEnabled" class="config-section">
							<div class="section-header">
								<feather-icon icon="LayersIcon" svgClasses="w-6 h-6 text-success" />
								<h3 class="section-title">{{ $t("Clustering.DetailedConfig") }}</h3>
								<div class="header-actions ml-auto">
									<vs-button size="small" type="flat" @click="expandAll">
										{{ $t("Clustering.ExpandAll") }}
									</vs-button>
									<vs-button size="small" type="flat" @click="collapseAll">
										{{ $t("Clustering.CollapseAll") }}
									</vs-button>
								</div>
							</div>

							<div v-if="clusterConfig.length > 0" class="clusters-container">
								<!-- Info Banner -->
								<div v-if="clusterConfig.length > clustersPerPage" class="pagination-info">
									<feather-icon icon="InfoIcon" svgClasses="w-4 h-4 mr-2" />
									<span>
										{{ $t("Clustering.Showing") }}
										{{ (currentClusterPage - 1) * clustersPerPage + 1 }} -
										{{ Math.min(currentClusterPage * clustersPerPage, clusterConfig.length) }}
										{{ $t("Clustering.Of") }}
										{{ clusterConfig.length }}
										{{ $t("Clustering.Clusters") }}
									</span>
								</div>

								<div v-for="(cluster, cIndex) in visibleClusters" :key="cIndex" class="cluster-card">
									<!-- Cluster Header -->
									<div class="cluster-header" @click="toggleCluster(cIndex)">
										<div class="header-left">
											<feather-icon
												:icon="
													activeClusterIndex === cIndex
														? 'ChevronDownIcon'
														: 'ChevronRightIcon'
												"
												svgClasses="w-5 h-5 mr-2 transition-transform"
											/>
											<span class="cluster-name">{{
												cluster.identification || `Cluster ${cIndex + 1}`
											}}</span>
										</div>
										<div class="header-right">
											<vs-chip
												:color="
													getMicroClusterCount(cIndex) >= totalMicroClusters
														? 'danger'
														: getMicroClusterCount(cIndex) > 0
														? 'success'
														: 'warning'
												"
												size="small"
											>
												<feather-icon icon="GridIcon" svgClasses="w-3 h-3 mr-1" />
												{{ getMicroClusterCount(cIndex) }} /
												{{ totalMicroClusters }} Micro-Clusters
											</vs-chip>
										</div>
									</div>

									<!-- Cluster Content (Expandable) -->
									<transition name="expand">
										<div v-show="activeClusterIndex === cIndex" class="cluster-content">
											<!-- Cluster Identification -->
											<div class="form-field">
												<label class="field-label">{{ $t("Clustering.Identification") }}</label>
												<vs-input
													v-model="cluster.identification"
													class="w-full"
													:placeholder="`Cluster ${cIndex + 1}`"
													@input="updateData"
												/>
											</div>

											<!-- Micro Clusters Section -->
											<div class="micro-clusters-section">
												<div class="micro-clusters-header">
													<h5 class="font-semibold flex items-center">
														<feather-icon icon="GridIcon" svgClasses="w-4 h-4 mr-2" />
														Micro Clusters ({{ getMicroClusterCount(cIndex) }} /
														{{ totalMicroClusters }})
													</h5>
												</div>

												<!-- Micro Cluster Cards Grid -->
												<div
													v-if="
														cluster.micro_cluster_config &&
														cluster.micro_cluster_config.length > 0
													"
													class="micro-clusters-container"
												>
													<!-- Info Banner -->
													<div
														v-if="
															cluster.micro_cluster_config.length > microClustersPerPage
														"
														class="pagination-info mb-4"
													>
														<feather-icon icon="InfoIcon" svgClasses="w-4 h-4 mr-2" />
														<span>
															{{ $t("Clustering.Showing") || "Showing" }}
															{{
																((cluster.currentPage || 1) - 1) *
																	microClustersPerPage +
																1
															}}
															-
															{{
																Math.min(
																	(cluster.currentPage || 1) * microClustersPerPage,
																	cluster.micro_cluster_config.length
																)
															}}
															{{ $t("Clustering.Of") || "of" }}
															{{ cluster.micro_cluster_config.length }}
															{{ $t("Clustering.MicroClusters") || "micro-clusters" }}
														</span>
													</div>

													<div class="micro-clusters-grid">
														<div
															v-for="(micro, mIndex) in getVisibleMicroClusters(cluster)"
															:key="mIndex"
															class="micro-cluster-card"
														>
															<div class="card-body">
																<!-- Identification -->
																<div class="mb-3">
																	<label class="micro-label">{{
																		$t("Clustering.Identification")
																	}}</label>
																	<vs-input
																		v-model="micro.identification"
																		class="w-full"
																		size="small"
																		:placeholder="`MC ${cIndex + 1}-${
																			getAbsoluteIndex(cluster, mIndex) + 1
																		}`"
																		@input="updateData"
																	/>
																</div>
																<!-- Capacity with Presets -->
																<div>
																	<label class="micro-label">{{
																		$t("Clustering.Capacity")
																	}}</label>
																	<div class="capacity-input-group">
																		<vs-input
																			v-model.number="micro.capacity"
																			type="number"
																			min="1"
																			max="2147483647"
																			class="capacity-input"
																			size="small"
																			@input="
																				validateCapacity(
																					cIndex,
																					getAbsoluteIndex(cluster, mIndex),
																					$event
																				)
																			"
																		/>
																	</div>
																	<!-- Preset Buttons -->
																	<div class="preset-buttons">
																		<vs-button
																			v-for="preset in capacityPresets"
																			:key="preset.value"
																			size="small"
																			type="flat"
																			@click="
																				setCapacity(
																					cIndex,
																					getAbsoluteIndex(cluster, mIndex),
																					preset.value
																				)
																			"
																		>
																			{{ preset.label }}
																		</vs-button>
																	</div>
																</div>
															</div>
														</div>
													</div>
													<!-- Pagination -->
													<div
														v-if="
															cluster.micro_cluster_config.length > microClustersPerPage
														"
														class="pagination-container mt-4 flex justify-center"
													>
														<vs-pagination
															:total="
																Math.ceil(
																	cluster.micro_cluster_config.length /
																		microClustersPerPage
																)
															"
															v-model="cluster.currentPage"
															:max="7"
														></vs-pagination>
													</div>
												</div>

												<!-- Empty State -->
												<div v-else class="empty-state">
													<feather-icon
														icon="InboxIcon"
														svgClasses="w-12 h-12 mb-3 opacity-25"
													/>
													<p class="text-sm text-gray-500 mb-3">
														{{ $t("Clustering.NoMicroClustersAssigned") }}
													</p>
												</div>
											</div>
										</div>
									</transition>
								</div>
							</div>

							<!-- Pagination for Clusters -->
							<div
								v-if="clusterConfig.length > clustersPerPage"
								class="pagination-container mt-4 flex justify-center"
							>
								<vs-pagination
									:total="Math.ceil(clusterConfig.length / clustersPerPage)"
									v-model="currentClusterPage"
									:max="7"
								></vs-pagination>
							</div>
						</div>
					</transition>
				</vx-card>
			</div>

			<!-- Loading Overlay -->
			<div v-if="isLoading" class="loading-overlay">
				<div class="loading-content">
					<vs-progress indeterminate color="primary" class="mb-4" />
					<h4 class="text-primary font-semibold">
						{{ $t("Clustering.GeneratingConfig") || "Generating Configuration..." }}
					</h4>
				</div>
			</div>

			<!-- Validation Alert -->
			<transition name="slide-down">
				<vs-alert
					v-if="!isValid"
					color="danger"
					icon-pack="feather"
					icon="icon-alert-triangle"
					class="validation-alert"
				>
					{{ $t("Wizard.Steps.Config.ValidationError") }}
				</vs-alert>
			</transition>
		</div>
	</div>
</template>

<script>
export default {
	name: "WizardStepConfig",
	props: {
		wizardData: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			clusteringName: this.wizardData.clusteringName || "",
			totalClusters: this.wizardData.totalClusters || 3,
			totalMicroClusters: this.wizardData.totalMicroClusters || 1,
			capacitiesEnabled: false,
			clusterConfig: this.wizardData.cluster_config || [],
			activeClusterIndex: 0,
			clusterDebounceTimer: null,
			microClusterDebounceTimer: null,
			// Pagination for clusters
			clustersPerPage: 20,
			// Pagination for micro-clusters
			microClustersPerPage: 50,
			capacityPresets: [
				{label: "100", value: 100},
				{label: "1000", value: 1000},
				{label: "10000", value: 10000},
				{label: "100000", value: 100000},
				{label: "Max", value: 2147483647},
			],
			isLoading: false,
			currentClusterPage: 1,
		};
	},
	computed: {
		totalDemand() {
			return this.wizardData.selectedPoints.reduce((sum, p) => sum + (parseInt(p.demand) || 0), 0);
		},
		totalMicroClustersAllocated() {
			return this.clusterConfig.reduce((sum, cluster) => {
				return sum + (cluster.micro_cluster_config ? cluster.micro_cluster_config.length : 0);
			}, 0);
		},
		isValid() {
			return this.clusteringName.length > 0 && this.totalClusters > 0;
		},
		visibleClusters() {
			const start = (this.currentClusterPage - 1) * this.clustersPerPage;
			const end = start + this.clustersPerPage;
			return this.clusterConfig.slice(start, end);
		},
	},
	watch: {
		// Reset micro-cluster pagination when active cluster changes
		activeClusterIndex() {
			this.displayedMicroClustersCount = this.microClustersPerPage;
		},
		capacitiesEnabled() {
			this.updateData();
		},
	},
	created() {
		if (this.clusterConfig.length === 0 && this.totalMicroClusters > 0) {
			this.generateClusterConfig();
		} else if (this.clusterConfig.length === 0) {
			this.initializeClusterConfig();
		}
	},
	beforeDestroy() {
		// Clean up timers to prevent memory leaks
		if (this.clusterDebounceTimer) {
			clearTimeout(this.clusterDebounceTimer);
		}
		if (this.microClusterDebounceTimer) {
			clearTimeout(this.microClusterDebounceTimer);
		}
	},
	methods: {
		getAbsoluteIndex(cluster, mIndex) {
			return ((cluster.currentPage || 1) - 1) * this.microClustersPerPage + mIndex;
		},
		getVisibleMicroClusters(cluster) {
			if (!cluster || !cluster.micro_cluster_config) return [];
			const page = cluster.currentPage || 1;
			const perPage = this.microClustersPerPage;
			const start = (page - 1) * perPage;
			const end = start + perPage;
			return cluster.micro_cluster_config.slice(start, end);
		},
		hasMoreMicroClusters(cluster) {
			if (!cluster || !cluster.micro_cluster_config) return false;
			return this.displayedMicroClustersCount < cluster.micro_cluster_config.length;
		},
		initializeClusterConfig() {
			const clusters = [];
			for (let i = 0; i < this.totalClusters; i++) {
				clusters.push({
					identification: `Cluster ${i + 1}`,
					micro_cluster_config: [],
				});
			}
			this.clusterConfig = clusters;
		},
		generateClusterConfig() {
			if (this.totalMicroClusters <= 0) {
				this.clusterConfig = [];
				return;
			}

			this.isLoading = true;

			// Use setTimeout to allow UI to update with loading state
			setTimeout(() => {
				const clusters = [];
				const microClustersPerCluster = this.totalMicroClusters;

				for (let i = 0; i < this.totalClusters; i++) {
					const microClusters = [];
					for (let j = 0; j < microClustersPerCluster; j++) {
						microClusters.push({
							identification: `MC ${i + 1}-${j + 1}`,
							capacity: 1,
						});
					}
					clusters.push({
						identification: `Cluster ${i + 1}`,
						micro_cluster_config: microClusters,
						currentPage: 1, // Initialize pagination
					});
				}

				this.clusterConfig = clusters;
				this.resetPagination();
				this.updateData();
				this.isLoading = false;
			}, 50);
		},
		handleClusterCountChange() {
			const currentCount = this.clusterConfig.length;
			const newCount = this.totalClusters;

			if (newCount < currentCount) {
				// Remove clusters if count decreases
				this.clusterConfig = this.clusterConfig.slice(0, newCount);
				this.resetPagination();
				this.updateData();
			} else if (newCount > currentCount) {
				this.isLoading = true;

				setTimeout(() => {
					// Add new clusters if count increases
					const microClustersPerCluster = this.totalMicroClusters;

					for (let i = currentCount; i < newCount; i++) {
						const microClusters = [];
						if (microClustersPerCluster > 0) {
							for (let j = 0; j < microClustersPerCluster; j++) {
								microClusters.push({
									identification: `MC ${i + 1}-${j + 1}`,
									capacity: 1,
								});
							}
						}

						this.clusterConfig.push({
							identification: `Cluster ${i + 1}`,
							micro_cluster_config: microClusters,
							currentPage: 1, // Initialize pagination
						});
					}

					this.resetPagination();
					this.updateData();
					this.isLoading = false;
				}, 50);
			} else {
				this.resetPagination();
				this.updateData();
			}
		},
		handleMicroClusterTotalChange() {
			this.generateClusterConfig();
		},
		getMicroClusterCount(clusterIndex) {
			const cluster = this.clusterConfig[clusterIndex];
			return cluster && cluster.micro_cluster_config ? cluster.micro_cluster_config.length : 0;
		},
		expandAll() {
			// Vuesax accordion doesn't support this directly, but we can cycle through
			this.activeClusterIndex = 0;
		},
		collapseAll() {
			this.activeClusterIndex = -1;
		},
		toggleCluster(index) {
			this.activeClusterIndex = this.activeClusterIndex === index ? -1 : index;
		},
		setCapacity(clusterIndex, microIndex, value) {
			const cluster = this.clusterConfig[clusterIndex];
			if (cluster && cluster.micro_cluster_config && cluster.micro_cluster_config[microIndex]) {
				cluster.micro_cluster_config[microIndex].capacity = value;
				this.updateData();
			}
		},
		resetPagination() {
			this.currentClusterPage = 1;
		},
		validateAndUpdateClusters() {
			// Clear any existing timer
			if (this.clusterDebounceTimer) {
				clearTimeout(this.clusterDebounceTimer);
			}

			// Ensure value is within bounds
			// Note: While the backend may accept 2147483647, creating that many
			// cluster objects in the UI would freeze the browser. We limit to 10000.
			if (this.totalClusters < 1) {
				this.totalClusters = 1;
			} else if (this.totalClusters > 10000) {
				this.totalClusters = 10000;
				this.$vs.notify({
					title: this.$t("Warning"),
					text:
						this.$t("Clustering.MaxClustersWarning") ||
						"Maximum 10,000 clusters allowed for UI performance",
					color: "warning",
					position: "top-right",
				});
			}

			// Debounce the actual cluster generation to avoid freezing while typing
			this.clusterDebounceTimer = setTimeout(() => {
				this.handleClusterCountChange();
			}, 500); // Wait 500ms after user stops typing
		},
		validateAndUpdateMicroClusters() {
			// Clear any existing timer
			if (this.microClusterDebounceTimer) {
				clearTimeout(this.microClusterDebounceTimer);
			}

			// Ensure value is within bounds
			if (this.totalMicroClusters < 1) {
				this.totalMicroClusters = 1;
			} else if (this.totalMicroClusters > 10000) {
				this.totalMicroClusters = 10000;
				this.$vs.notify({
					title: this.$t("Warning"),
					text:
						this.$t("Clustering.MaxMicroClustersWarning") ||
						"Maximum 10,000 micro-clusters allowed for UI performance",
					color: "warning",
					position: "top-right",
				});
			}

			// Debounce the actual micro cluster generation to avoid freezing while typing
			this.microClusterDebounceTimer = setTimeout(() => {
				this.handleMicroClusterTotalChange();
			}, 500); // Wait 500ms after user stops typing
		},
		validateCapacity(clusterIndex, microIndex, event) {
			const cluster = this.clusterConfig[clusterIndex];
			if (cluster && cluster.micro_cluster_config && cluster.micro_cluster_config[microIndex]) {
				let value = cluster.micro_cluster_config[microIndex].capacity;
				// Capacity can be very large (it's just a number, not creating UI elements)
				if (value < 1) {
					cluster.micro_cluster_config[microIndex].capacity = 1;
				} else if (value > 2147483647) {
					cluster.micro_cluster_config[microIndex].capacity = 2147483647;
				}
			}
			this.updateData();
		},
		updateData() {
			let configToSend = null;

			if (this.capacitiesEnabled && this.clusterConfig.length > 0) {
				// Create a clean copy of cluster config without UI state like currentPage
				configToSend = this.clusterConfig.map((cluster) => {
					const {currentPage, ...rest} = cluster;
					return rest;
				});
			}

			this.$emit("update-data", {
				clusteringName: this.clusteringName,
				totalClusters: this.totalClusters,
				totalMicroClusters: this.totalMicroClusters,
				cluster_config: configToSend,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.wizard-step-config {
	.step-header {
		text-align: center;
		margin-bottom: 32px;

		.step-title {
			font-size: 1.5rem;
			font-weight: 700;
			color: #333;
			margin-bottom: 8px;
		}

		.step-description {
			font-size: 0.95rem;
			color: #666;
		}
	}

	.step-content {
		width: 100%;

		.summary-panel {
			background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
			color: white;
			padding: 32px;
			border-radius: 16px;
			margin-bottom: 32px;

			.panel-title {
				font-size: 1.5rem;
				margin-bottom: 24px;
				text-align: center;
			}

			.summary-grid {
				display: grid;
				grid-template-columns: repeat(2, 1fr);
				gap: 24px;

				.summary-item {
					background: rgba(255, 255, 255, 0.15);
					padding: 20px;
					border-radius: 12px;
					display: flex;
					align-items: center;
					gap: 16px;

					.summary-content {
						display: flex;
						flex-direction: column;

						.summary-value {
							font-size: 2rem;
							font-weight: 700;
							line-height: 1;
							margin-bottom: 8px;
						}

						.summary-label {
							font-size: 0.85rem;
							opacity: 0.9;
						}
					}
				}
			}
		}

		.config-form {
			// Section Styles
			.config-section {
				&:not(:last-child) {
					margin-bottom: 16px;
				}

				.section-header {
					display: flex;
					align-items: center;
					gap: 12px;
					margin-bottom: 24px;
					padding-bottom: 12px;
					border-bottom: 2px solid #e0e0e0;

					.section-title {
						font-size: 1.25rem;
						font-weight: 700;
						color: #333;
						margin: 0;
					}

					.header-actions {
						display: flex;
						gap: 8px;
					}
				}
			}

			// Form Field Styles
			.form-field {
				margin-bottom: 24px;

				.field-label {
					display: block;
					font-weight: 600;
					margin-bottom: 8px;
					color: #333;

					&.required::after {
						content: " *";
						color: #f44336;
					}
				}

				.field-help {
					display: block;
					margin-top: 6px;
					color: #666;
					font-size: 0.85rem;
				}

				// Toggle Field
				.toggle-field {
					display: flex;
					justify-content: space-between;
					align-items: center;
					padding: 16px;
					background: #f8f9fa;
					border-radius: 8px;
					border: 1px solid #e0e0e0;

					.toggle-header {
						display: flex;
						align-items: center;
					}
				}

				// Input with Info
				.input-with-info {
					display: flex;
					align-items: center;
					gap: 12px;

					.cluster-input {
						width: 150px;
					}

					.input-info {
						flex-shrink: 0;
					}
				}

				// Micro Cluster Config
				&.micro-cluster-config {
					background: #f0f7ff;
					padding: 20px;
					border-radius: 12px;
					border-left: 4px solid rgba(var(--vs-primary), 1);
				}

				.action-buttons {
					display: flex;
					gap: 8px;
				}
			}

			// Clusters Container
			.clusters-container {
				display: flex;
				flex-direction: column;
				gap: 16px;

				.pagination-info {
					display: flex;
					align-items: center;
					padding: 12px 16px;
					background: #e3f2fd;
					border-left: 4px solid rgba(var(--vs-primary), 1);
					border-radius: 4px;
					color: #1976d2;
					font-size: 0.9rem;
					font-weight: 500;
					margin-bottom: 8px;
				}

				.cluster-card {
					background: white;
					border: 1px solid #e0e0e0;
					border-radius: 12px;
					overflow: hidden;
					transition: all 0.3s ease;

					&:hover {
						box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
					}

					.cluster-header {
						display: flex;
						justify-content: space-between;
						align-items: center;
						padding: 16px 20px;
						background: linear-gradient(
							135deg,
							rgba(var(--vs-primary), 0.05) 0%,
							rgba(var(--vs-success), 0.05) 100%
						);
						cursor: pointer;
						transition: all 0.3s ease;

						&:hover {
							background: linear-gradient(
								135deg,
								rgba(var(--vs-primary), 0.1) 0%,
								rgba(var(--vs-success), 0.1) 100%
							);
						}

						.header-left {
							display: flex;
							align-items: center;

							.cluster-name {
								font-weight: 600;
								font-size: 1.05rem;
								color: #333;
							}
						}

						.header-right {
							display: flex;
							align-items: center;
							gap: 8px;
						}
					}

					.cluster-content {
						padding: 20px;
						background: #fafafa;
					}
				}
			}

			// Micro Clusters Section
			.micro-clusters-section {
				.micro-clusters-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 16px;
					padding-bottom: 12px;
					border-bottom: 2px solid #e0e0e0;
				}

				.micro-clusters-grid {
					display: grid;
					grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
					gap: 16px;
				}

				.micro-cluster-card {
					background: white;
					border: 1px solid #e0e0e0;
					border-radius: 8px;
					overflow: hidden;
					transition: all 0.3s ease;

					&:hover {
						box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
						transform: translateY(-2px);
					}

					.card-header {
						display: flex;
						justify-content: space-between;
						align-items: center;
						padding: 10px 12px;
						background: linear-gradient(
							135deg,
							rgba(var(--vs-primary), 0.08) 0%,
							rgba(var(--vs-success), 0.08) 100%
						);
						border-bottom: 1px solid #e0e0e0;

						.card-title {
							font-weight: 700;
							font-size: 0.9rem;
							color: rgba(var(--vs-primary), 1);
						}
					}

					.card-body {
						padding: 12px;

						.micro-label {
							display: block;
							font-size: 0.75rem;
							font-weight: 700;
							color: #666;
							text-transform: uppercase;
							margin-bottom: 6px;
						}

						.capacity-input-group {
							margin-bottom: 8px;

							.capacity-input {
								width: 100%;
							}
						}

						.preset-buttons {
							display: flex;
							gap: 4px;
							flex-wrap: wrap;

							button {
								flex: 1;
								min-width: 50px;
								font-size: 0.75rem;
								padding: 4px 8px;
							}
						}
					}
				}

				.empty-state {
					text-align: center;
					padding: 40px 20px;
					background: white;
					border-radius: 8px;
					border: 2px dashed #e0e0e0;
				}
			}

			// Load More Container
			.load-more-container {
				display: flex;
				justify-content: center;
				padding: 24px;
				margin-top: 16px;
			}
		}

		.validation-alert {
			margin-top: 24px;
		}

		.optional-badge {
			background: rgba(var(--vs-success), 1);
			color: white;
			padding: 2px 8px;
			border-radius: 12px;
			font-size: 0.7rem;
			margin-left: 8px;
		}
	}
}

// Transition Animations
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
	transform: translateY(-20px);
	opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease;
	overflow: hidden;
}

.expand-enter,
.expand-leave-to {
	max-height: 0;
	opacity: 0;
}

@media screen and (max-width: 768px) {
	.wizard-step-config {
		.summary-panel .summary-grid {
			grid-template-columns: 1fr !important;
		}
	}
}
</style>
